
import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { extractSeconds } from "./time-utils";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatMinutes(minutes: number): string {
  const safeValue = Math.max(Math.floor(minutes), 0);
  const hours = Math.floor(safeValue / 60);
  const remainingMinutes = safeValue % 60;

  const parts: string[] = [];
  if (hours > 0) {
    parts.push(`${hours} ч`);
  }
  if (remainingMinutes > 0 || parts.length === 0) {
    parts.push(`${remainingMinutes} мин`);
  }

  return parts.join(" ");
}

export function formatBytes(bytes: number, decimals = 2): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

export function formatDuration(duration: unknown): string {
  // Преобразуем interval в секунды
  const seconds = extractSeconds(duration);
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);

  const parts: string[] = [];

  if (hours > 0) {
    parts.push(`${hours}:${minutes.toString().padStart(2, '0')}`);
  } else {
    parts.push(`${minutes}`);
  }

  parts.push(remainingSeconds.toString().padStart(2, '0'));

  return parts.join(':');
}

interface TranscriptSegment {
  speakerId: number;
  startTime: number;
  endTime: number;
  text: string;
}

export function generateMockTranscript(durationSeconds: number): TranscriptSegment[] {
  const transcript = [];
  let currentTime = 0;
  
  while (currentTime < durationSeconds) {
    const segmentDuration = Math.min(
      Math.floor(Math.random() * 30) + 5, // 5-35 seconds per segment
      durationSeconds - currentTime
    );
    
    transcript.push({
      speakerId: Math.floor(Math.random() * 3), // 0, 1, or 2
      startTime: currentTime,
      endTime: currentTime + segmentDuration,
      text: getRandomText(),
    });
    
    currentTime += segmentDuration;
  }
  
  return transcript;
}

function getRandomText(): string {
  const sentences = [
    "Я думаю, что этот проект имеет большой потенциал.",
    "Мы должны сосредоточиться на решении этой проблемы немедленно.",
    "Давайте обсудим сроки выполнения и распределим задачи между командой.",
    "Что вы думаете о предложенном решении?",
    "На мой взгляд, нам нужно пересмотреть бюджет проекта.",
    "Результаты последних тестов показывают значительное улучшение производительности.",
    "Я согласен с предыдущим оратором и хотел бы добавить несколько соображений.",
    "Мы можем оптимизировать процесс, если внедрим новую методологию.",
    "Клиент запрашивает дополнительные функции, которые не были включены в исходное техническое задание.",
    "Я предлагаю отложить принятие решения до следующей встречи, когда у нас будет больше данных."
  ];

  // Return 1-3 random sentences
  const count = Math.floor(Math.random() * 3) + 1;
  const result = [];

  for (let i = 0; i < count; i++) {
    const randomIndex = Math.floor(Math.random() * sentences.length);
    result.push(sentences[randomIndex]);
  }

  return result.join(" ");
}

/**
 * Encodes a string to base64 with proper Unicode support
 */
export function base64Encode(str: string): string {
  // Use TextEncoder for proper Unicode handling
  const utf8Bytes = new TextEncoder().encode(str);
  const binaryString = Array.from(utf8Bytes, byte => String.fromCharCode(byte)).join('');
  return btoa(binaryString);
}

/**
 * Decodes a base64 string to original string with proper Unicode support
 */
export function base64Decode(base64: string): string {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return new TextDecoder().decode(bytes);
}
